<script module lang="ts">
  export const classicTutorialModal = new ModalQueryState('rules', { layer: 700 });
</script>

<script lang="ts">
  import ArrowRight from '~icons/mdi/arrow-right';
  import ArrowDown from '~icons/mdi/arrow-down';
  import ArrowBottomRight from '~icons/mdi/arrow-bottom-right';
  import ArrowLeft from '~icons/mdi/arrow-left';
  import Numeric4 from '~icons/mdi/numeric-4-box';
  import Numeric5 from '~icons/mdi/numeric-5-box';
  import Numeric6 from '~icons/mdi/numeric-6-box';
  import Numeric7 from '~icons/mdi/numeric-7-box';
  import HelpCircle from '~/icons/HelpCircle.svelte';
  import Shuffle from '../Shuffle.svelte';
  import ActionButton from '~/components/ActionButton.svelte';
  import Close from '~icons/mdi/close';
  import Home from '~icons/mdi/home';
  import Play from '~icons/mdi/play';
  import School from '~icons/mdi/school';
  import Swaps from '../Swaps.svelte';
  import Word from '~/components/Word.svelte';
  import Star from '~/icons/Star.svelte';
  import { scoreTile } from '~/algorithms/letters';
  import SwapRules from './SwapRules.svelte';
  import { ModalQueryState } from '~/lib/nav.svelte';
  import { onboarded, tutorialViewed } from '../state.svelte';
  import TileFlip from '../TileFlip.svelte';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import type { ModalSection } from '~/types';
  import Directory from '~/components/Directory.svelte';
  import SplashLogo from '~/components/SplashLogo.svelte';
  import SlidePanel from '~/components/SlidePanel.svelte';
  import ChevronDownIcon from '~icons/feather/chevron-down';
  import DimensionsContext from '~/utils/DimensionsContext.svelte';

  const sections: ModalSection[] = [
    {
      id: 'swapping-tiles',
      desc: 'Swapping tiles',
    },
    {
      id: 'objective',
      desc: 'Objective of the game',
    },
    {
      id: 'word-rules',
      desc: 'Making words',
    },
    {
      id: 'swap-cost',
      desc: 'Swap cost',
    },
    {
      id: 'special-tiles',
      desc: 'Special tiles',
    },
    {
      id: 'flipping-tiles',
      desc: 'Flipping tiles',
    },
    {
      id: 'swap-reward',
      desc: 'Earning swaps',
    },
    {
      id: 'shuffling',
      desc: 'Shuffling the board',
    },
  ];

  const fromHome = $derived(classicTutorialModal.value === 'b');

  const handleToggle = (open: boolean) => {
    if (open) {
      classicTutorialModal.push();
    } else {
      handleClose();
    }
  };

  const handleBack = async () => {
    await classicTutorialModal.pop();
  };

  const handleClose = async () => {
    if (fromHome) {
      goto(page.route.id!, { shallow: true, replace: true });
    } else {
      await classicTutorialModal.pop();
    }
  };

  const handleTutorial = () => {
    onboarded.current = false;
    handleClose();
  };
</script>

<SlidePanel
  open={classicTutorialModal.open}
  scrollOffscreen={tutorialViewed.current}
  id="classic-tutorial-modal"
  onToggle={handleToggle}
  maxWidth="32em"
>
  {#snippet title()}
    <SplashLogo animate gameOver />
  {/snippet}
  {#snippet handle()}
    <h1 class="subtitle">
      <HelpCircle />How to play
    </h1>
  {/snippet}
  {#snippet content()}
    <div class="content">
      {#if tutorialViewed.current}
        <Directory sections={sections} />
      {/if}
      <h2 id="swapping-tiles">Swap letters by tapping or dragging them</h2>
      <Swaps play={classicTutorialModal.open} />
      <h2 id="objective">
        Get the highest score possible before you run out of swaps!
      </h2>
      <p>
        Making a word immediately scores it and removes it from the board. Word
        scores can be multiplied by multiplier tiles.
      </p>
      <h2 id="word-rules">How are words counted?</h2>
      <div>
        <ul>
          <li class="yes">along rows <ArrowRight />and columns <ArrowDown /></li>
          <li class="yes">at least <Numeric4 size="1.5em" />letters long</li>
          <li class="no">not along diagonals <ArrowBottomRight /></li>
          <li class="no">not read backwards <ArrowLeft /></li>
        </ul>
      </div>
      <h2 id="swap-cost">Swap cost</h2>
      <SwapRules />
      <div></div>
      <h2 id="special-tiles">Special Tiles</h2>
      <p>Double-letter tiles act as two consecutive letters</p>
      <div class="word">
        <DimensionsContext {...{ width: 360, height: 80 }}>
          <Word
            word={['AD', 'V', 'E', 'RB'].map((letter, id) => ({
              letter,
              points: scoreTile(letter, 'classic'),
              id: id.toString(),
            }))}
          />
        </DimensionsContext>
      </div>
      <h2 id="flipping-tiles">Flipping tiles</h2>
      <p>
        Swap the order of letters on a tile by double-clicking. This costs one
        swap.
      </p>
      <TileFlip />
      <div>
        <h2 id="swap-reward">Earn back swaps based on your word length</h2>
        <ul class="swap-chart">
          <li>
            <Numeric4 size={24} /><span>letters =</span><span
              >1 <Star size={24} class="yellow" /></span
            >
          </li>
          <li>
            <Numeric5 size={24} /><span>letters =</span><span
              >2<Star size={24} class="yellow" /></span
            >
          </li>
          <li>
            <Numeric6 size={24} /><span>letters =</span><span
              >4<Star size={24} class="yellow" /></span
            >
          </li>
          <li>
            <Numeric7 size={24} /><span>letters =</span><span
              >8 <Star size={24} class="yellow" /></span
            >
          </li>
        </ul>
      </div>
      <div>
        <h2 id="shuffling">Use a shuffle to mix up the letters on the board</h2>
        <Shuffle play={classicTutorialModal.open} />
        <p class="secondary">
          Earn back shuffles by completing full-row, full-column, or
          intersecting words
        </p>

        <p class="secondary">Shuffles do not use up swaps</p>
      </div>
    </div>
  {/snippet}
  {#snippet controls()}
    {#if fromHome}
      <ActionButton onClick={handleBack}>
        <Home />
        Home
      </ActionButton>
      <ActionButton onClick={handleTutorial}>
        <School />
        Tutorial
      </ActionButton>
      <ActionButton onClick={handleClose}>
        Play
        <Play />
      </ActionButton>
    {:else}
      <ActionButton onClick={handleTutorial}>
        <School />
        Tutorial
      </ActionButton>
      <ActionButton onClick={handleBack}>
        {#if tutorialViewed.current}
          <Close />
          Close
        {:else}
          <ChevronDownIcon />
          Hide
        {/if}
      </ActionButton>
    {/if}
  {/snippet}
</SlidePanel>

<style lang="scss">
  * {
    text-align: center;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 1em;
    & :global(.tile-container),
    & :global(.tile) {
      z-index: unset !important;
    }
    :global(h1),
    :global(h2),
    :global(h3),
    :global(h4) {
      width: 100%;
    }
  }
  h2 {
    margin-top: 2em;
    font-size: 1.125rem;
  }
  ul {
    padding-left: 0;
  }
  li {
    text-align: left;
    display: flex;
    align-items: center;
    &.yes::before {
      content: '✔️';
      color: #1b5e20;
      margin-right: 1em;
    }
    &.no::before {
      content: '❌';
      color: #b71c1c;
      margin-right: 1em;
    }
  }
  .word {
    pointer-events: none;
    position: relative;
    width: 100%;
    height: 3rem;
  }
  .swap-chart {
    font-size: 1.25rem;
    display: flex;
    width: fit-content;
    margin: 0 auto;
    flex-direction: column;
    gap: 0.25em;
    li {
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: 1.5em auto 2.5em;
      & > * {
        display: flex;
        align-items: center;
        justify-content: end;
      }
      & :last-of-type {
        display: flex;
        gap: 0.25em;
      }
    }
  }
  .subtitle {
    margin: 0;
    margin-left: -2em;
    gap: 1em;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
